import { Box, Button, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { getModificationColor } from "./scenarioApprovalUtils";

export default function ModificationEntityList({
	label,
	items = [],
	addedLabel = "Added",
	deletedLabel = "Deleted",
	hideSortByNumber = false,
}) {
	const [sortBy, setSortBy] = useState("name");
	const [sortAsc, setSortAsc] = useState(true);
	const [filter, setFilter] = useState(null);

	useEffect(() => {
		setFilter(null);
		setSortBy("name");
		setSortAsc(true);
	}, [items]);

	const sortedFiltered = useMemo(() => {
		let list = [...items];
		if (filter === "added") {
			list = list.filter((i) => i.isActive === 1);
		} else if (filter === "deleted") {
			list = list.filter((i) => i.isActive === -1);
		}
		list.sort((a, b) => {
			const av = sortBy === "number" ? a.number : a.name;
			const bv = sortBy === "number" ? b.number : b.name;
			const cmp = String(av).localeCompare(String(bv), undefined, {
				numeric: true,
				sensitivity: "base",
			});
			return sortAsc ? cmp : -cmp;
		});
		return list;
	}, [items, filter, sortBy, sortAsc]);

	const handleSort = (type) => {
		if (sortBy === type) {
			setSortAsc((prev) => !prev);
		} else {
			setSortBy(type);
			setSortAsc(true);
		}
	};

	return (
		<Box sx={{ mb: 2 }}>
			<Typography sx={{ fontWeight: "600", fontSize: "14px", mb: 1 }}>
				{label}
			</Typography>
			<Box
				sx={{
					display: "flex",
					flexWrap: "wrap",
					alignItems: "center",
					gap: 1,
					mb: 1,
				}}
			>
				<Typography
					sx={{ fontSize: "14px", cursor: "pointer" }}
					onClick={() => handleSort("name")}
					display="inline"
				>
					Sort by Name
				</Typography>
				{!hideSortByNumber ? (
					<>
						<Typography sx={{ fontSize: "14px" }} display="inline">
							|
						</Typography>
						<Typography
							sx={{ fontSize: "14px", cursor: "pointer" }}
							onClick={() => handleSort("number")}
							display="inline"
						>
							Sort by Number
						</Typography>
					</>
				) : null}
				<Button
					size="small"
					variant="outlined"
					disableElevation
					color="inherit"
					onClick={() => setFilter("added")}
					sx={{
						ml: 1,
						textTransform: "none",
						boxShadow: "none",
						...(filter === "added"
							? {
									bgcolor: "green.main",
									color: "#fff",
									borderColor: "green.main",
									"&:hover": {
										bgcolor: "#006600",
										borderColor: "#006600",
										color: "#fff",
										boxShadow: "none",
									},
									"&.Mui-focusVisible": {
										bgcolor: "#006600",
										borderColor: "#006600",
									},
								}
							: {
									bgcolor: "transparent",
									color: "green.main",
									borderColor: "green.main",
									"&:hover": {
										bgcolor: "rgba(0, 128, 0, 0.08)",
										borderColor: "green.main",
										color: "green.main",
										boxShadow: "none",
									},
								}),
					}}
				>
					{addedLabel}
				</Button>
				<Button
					size="small"
					variant="outlined"
					disableElevation
					color="inherit"
					onClick={() => setFilter("deleted")}
					sx={{
						textTransform: "none",
						boxShadow: "none",
						...(filter === "deleted"
							? {
									bgcolor: "red.main",
									color: "#fff",
									borderColor: "red.main",
									"&:hover": {
										bgcolor: "#cc0000",
										borderColor: "#cc0000",
										color: "#fff",
										boxShadow: "none",
									},
									"&.Mui-focusVisible": {
										bgcolor: "#cc0000",
										borderColor: "#cc0000",
									},
								}
							: {
									bgcolor: "transparent",
									color: "red.main",
									borderColor: "red.main",
									"&:hover": {
										bgcolor: "rgba(255, 0, 0, 0.08)",
										borderColor: "red.main",
										color: "red.main",
										boxShadow: "none",
									},
								}),
					}}
				>
					{deletedLabel}
				</Button>
				<Button
					size="small"
					variant="outlined"
					onClick={() => setFilter(null)}
					sx={{
						textTransform: "none",
						color: "text.primary",
						borderColor: "grey.400",
					}}
				>
					Clear Filter
				</Button>
			</Box>
			<Box
				sx={{
					border: "1px solid",
					borderColor: "grey.400",
					borderRadius: 1,
					minHeight: 120,
					maxHeight: 200,
					overflowY: "auto",
					p: 1.5,
					bgcolor: "#fff",
					fontFamily: "monospace",
					fontSize: "13px",
				}}
			>
				{sortedFiltered.length === 0 ? (
					<Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
						No items.
					</Typography>
				) : (
					sortedFiltered.map((item) => (
						<Typography
							key={item.id}
							component="div"
							variant="body2"
							sx={{
								display: "block",
								lineHeight: 1.6,
								color: getModificationColor(item.isActive),
								whiteSpace: "pre-wrap",
								wordBreak: "break-word",
							}}
						>
							{item.line}
						</Typography>
					))
				)}
			</Box>
		</Box>
	);
}
